import React, { useContext, useState, useRef, useCallback, useEffect } from "react";
import { useFormik } from "formik";
import * as Yup from "yup";
import { Box, Link, Alert, Typography } from '@mui/material';
import LoadingButton from '@mui/lab/LoadingButton';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';

import useApi from "../services/api";
import { GlobalContext } from "../contexts/global-context";
import { AuthContext } from "../contexts/auth-context";

const CODE_LENGTH = 6;

function CodeInput({ value, onChange, disabled, error, autoFocus }) {
    const inputRefs = useRef([]);

    const focusIndex = useCallback((i) => {
        const clamped = Math.max(0, Math.min(i, CODE_LENGTH - 1));
        inputRefs.current[clamped]?.focus();
    }, []);

    const handleKeyDown = (i, e) => {
        if (e.key === 'Backspace') {
            e.preventDefault();
            const chars = value.split('');
            if (chars[i]) {
                chars[i] = '';
                onChange(chars.join(''));
            } else if (i > 0) {
                chars[i - 1] = '';
                onChange(chars.join(''));
                focusIndex(i - 1);
            }
        } else if (e.key === 'ArrowLeft') {
            e.preventDefault();
            focusIndex(i - 1);
        } else if (e.key === 'ArrowRight') {
            e.preventDefault();
            focusIndex(i + 1);
        }
    };

    const handleInput = (i, e) => {
        const raw = e.target.value.replace(/[^0-9]/g, '');
        if (!raw) return;

        const chars = value.split('');
        // Support paste into a single slot
        if (raw.length > 1) {
            const pasted = raw.slice(0, CODE_LENGTH);
            onChange(pasted.padEnd(0, ''));
            focusIndex(Math.min(pasted.length, CODE_LENGTH) - 1);
            return;
        }

        chars[i] = raw[0];
        const next = chars.join('');
        onChange(next);
        if (i < CODE_LENGTH - 1) {
            focusIndex(i + 1);
        }
    };

    const handlePaste = (e) => {
        e.preventDefault();
        const pasted = e.clipboardData.getData('text').replace(/[^0-9]/g, '').slice(0, CODE_LENGTH);
        if (pasted) {
            onChange(pasted);
            focusIndex(Math.min(pasted.length, CODE_LENGTH) - 1);
        }
    };

    const handleFocus = (i, e) => {
        e.target.select();
    };

    return (
        <Box sx={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            gap: 0,
            width: '100%',
        }}>
            <Box sx={{
                display: 'flex',
                border: '2px solid',
                borderColor: error ? 'error.main' : 'divider',
                borderRadius: '12px',
                overflow: 'hidden',
                transition: 'border-color 0.2s',
                '&:focus-within': {
                    borderColor: error ? 'error.main' : 'primary.main',
                },
            }}>
                {Array.from({ length: CODE_LENGTH }).map((_, i) => (
                    <React.Fragment key={i}>
                        {i === 3 && (
                            <Box sx={{
                                width: '1px',
                                bgcolor: error ? 'error.main' : 'divider',
                                my: 1.5,
                                mx: 0.5,
                                flexShrink: 0,
                                transition: 'background-color 0.2s',
                            }} />
                        )}
                        {i > 0 && i !== 3 && (
                            <Box sx={{
                                width: '1px',
                                bgcolor: error ? 'error.main' : 'divider',
                                my: 1,
                                flexShrink: 0,
                                transition: 'background-color 0.2s',
                            }} />
                        )}
                        <Box
                            component="input"
                            ref={(el) => { inputRefs.current[i] = el; }}
                            type="text"
                            inputMode="numeric"
                            autoComplete={i === 0 ? 'one-time-code' : 'off'}
                            autoFocus={autoFocus && i === 0}
                            value={value[i] || ''}
                            disabled={disabled}
                            onKeyDown={(e) => handleKeyDown(i, e)}
                            onInput={(e) => handleInput(i, e)}
                            onPaste={handlePaste}
                            onFocus={(e) => handleFocus(i, e)}
                            sx={{
                                width: '44px',
                                height: '56px',
                                border: 'none',
                                outline: 'none',
                                textAlign: 'center',
                                fontSize: '1.5rem',
                                fontWeight: 600,
                                fontFamily: '"Poppins", "Roboto", monospace',
                                color: 'text.primary',
                                bgcolor: 'transparent',
                                caretColor: 'primary.main',
                                '&:disabled': {
                                    opacity: 0.5,
                                },
                            }}
                        />
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    );
}

export function Emailed() {
    const { confirmEmail, resendConfirmEmail } = useApi();
    const { email, sessionToken, storeUser } = useContext(AuthContext);
    const { setShowAuthModal, setAuthModalType } = useContext(GlobalContext);
    const [generalError, setGeneralError] = useState({
        isErrorHidden: true,
        errorMessage: ""
    });
    const [resendLoading, setResendLoading] = useState(false);
    const [resendSuccess, setResendSuccess] = useState(false);
    const RESEND_COOLDOWN_SECONDS = 30;
    const [cooldownRemaining, setCooldownRemaining] = useState(RESEND_COOLDOWN_SECONDS);
    const cooldownRef = useRef(null);

    const startCooldown = useCallback(() => {
        setCooldownRemaining(RESEND_COOLDOWN_SECONDS);
        if (cooldownRef.current) clearInterval(cooldownRef.current);
        cooldownRef.current = setInterval(() => {
            setCooldownRemaining((prev) => {
                if (prev <= 1) {
                    clearInterval(cooldownRef.current);
                    cooldownRef.current = null;
                    return 0;
                }
                return prev - 1;
            });
        }, 1000);
    }, []);

    // Start cooldown on mount (code was just sent)
    useEffect(() => {
        startCooldown();
        return () => {
            if (cooldownRef.current) clearInterval(cooldownRef.current);
        };
    }, [startCooldown]);

    const formik = useFormik({
        initialValues: {
            code: "",
        },
        validationSchema: Yup.object({
            code: Yup.string()
                .required("Required")
                .matches(/^\d{6}$/, "Enter a 6-digit code"),
        }),
        onSubmit: (values) => {
            setGeneralError({ isErrorHidden: true, errorMessage: "" });

            confirmEmail(values.code, sessionToken)
                .then((response) => {
                    if (response.status === 200) {
                        storeUser(response.data);
                        setShowAuthModal(false);
                    }
                })
                .catch((error) => {
                    let errorMessage = "Verification failed. Please try again.";

                    if (error.response?.status === 429) {
                        errorMessage = "Too many attempts. Please try again later.";
                    } else if (error.response?.data?.errors && Array.isArray(error.response.data.errors)) {
                        const firstError = error.response.data.errors[0];
                        if (firstError?.message) {
                            errorMessage = firstError.message;
                        }
                    } else if (error.response?.data?.detail) {
                        errorMessage = error.response.data.detail;
                    } else if (error.code === "ERR_NETWORK") {
                        errorMessage = "Network error. Please try again.";
                    }

                    setGeneralError({
                        isErrorHidden: false,
                        errorMessage: errorMessage,
                    });
                })
                .finally(() => {
                    formik.setSubmitting(false);
                });
        },
    });

    const handleResend = () => {
        setResendLoading(true);
        setResendSuccess(false);
        setGeneralError({ isErrorHidden: true, errorMessage: "" });

        resendConfirmEmail(sessionToken)
            .then(() => {
                setResendSuccess(true);
                startCooldown();
            })
            .catch((error) => {
                let errorMessage = "Failed to resend code. Please try again.";

                if (error.response?.status === 429) {
                    errorMessage = "Too many requests. Please try again later.";
                    startCooldown();
                } else if (error.response?.status === 409) {
                    errorMessage = "Session expired. Please restart signup.";
                } else if (error.code === "ERR_NETWORK") {
                    errorMessage = "Network error. Please try again.";
                }

                setGeneralError({
                    isErrorHidden: false,
                    errorMessage: errorMessage,
                });
            })
            .finally(() => {
                setResendLoading(false);
            });
    };

    return (
        <Box sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            mt: 0,
            m: 'auto',
            textAlign: 'center',
            width: '350px'
        }}>
            {!generalError.isErrorHidden && (
                <Alert sx={{ mb: 1, width: '100%' }} severity="error">
                    {generalError.errorMessage}
                </Alert>
            )}
            {resendSuccess && (
                <Alert sx={{ mb: 1, width: '100%' }} severity="success">
                    A new code has been sent to your email.
                </Alert>
            )}
            <EmailOutlinedIcon color="primary" sx={{ fontSize: 60 }} />
            <Box sx={{ mb: 1, typography: 'h6' }}>
                Check your email
            </Box>
            {email && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                    {email}
                </Typography>
            )}
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
                Enter the 6-digit code we sent to verify your account
            </Typography>
            <form onSubmit={formik.handleSubmit} style={{ width: '100%' }}>
                <CodeInput
                    value={formik.values.code}
                    onChange={(val) => formik.setFieldValue('code', val)}
                    disabled={formik.isSubmitting}
                    error={formik.touched.code && Boolean(formik.errors.code)}
                    autoFocus
                />
                {formik.touched.code && formik.errors.code && (
                    <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
                        {formik.errors.code}
                    </Typography>
                )}
                <LoadingButton
                    sx={{ mt: 2 }}
                    variant="contained"
                    fullWidth
                    loading={formik.isSubmitting}
                    type="submit"
                >
                    <span>Verify</span>
                </LoadingButton>
            </form>
            <Box sx={{ mt: 3, display: 'flex', alignItems: 'center', gap: 0.5, typography: 'body2' }}>
                <span>Didn&apos;t get the code?</span>
                <LoadingButton
                    size="small"
                    loading={resendLoading}
                    disabled={cooldownRemaining > 0}
                    onClick={handleResend}
                    sx={{ textTransform: 'none', minWidth: 'auto' }}
                >
                    <span>{cooldownRemaining > 0 ? `Resend code (${cooldownRemaining}s)` : 'Resend Code'}</span>
                </LoadingButton>
            </Box>
            <Box sx={{
                mt: 2,
                mb: 1,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                typography: 'body2'
            }}>
                <Link
                    onClick={() => setAuthModalType("login")}
                    component="button"
                >
                    Back to login
                </Link>
            </Box>
        </Box>
    );
}
